import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  AlertTriangle,
  Plus, 
  Trash2, 
  MapPin,
  Eye,
  Loader2,
  Calendar,
  User,
  Users,
  FileText,
  Search,
  Filter,
  Building,
  Send,
  Upload,
  ZoomIn,
  DollarSign,
  CheckCircle2,
  XCircle,
  Clock,
  FileImage,
  File,
  ChevronDown,
  ChevronUp,
  Edit3,
  Shield,
  Gauge,
  Activity,
  Link2,
  Wrench,
  Target,
  TrendingUp,
  AlertCircle,
  FileDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Risk Category options
const CATEGORY_OPTIONS = [
  { value: 'safety', label: 'Safety' },
  { value: 'operational', label: 'Operational' },
  { value: 'financial', label: 'Financial' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'reputational', label: 'Reputational' },
];

// Risk Type options
const TYPE_OPTIONS = [
  { value: 'hazard', label: 'Hazard' },
  { value: 'failure_mode', label: 'Failure Mode' },
  { value: 'condition', label: 'Condition' },
  { value: 'process_gap', label: 'Process Gap' },
];

// Likelihood options
const LIKELIHOOD_OPTIONS = [
  { value: 'rare', label: 'Rare', score: 1 },
  { value: 'unlikely', label: 'Unlikely', score: 2 },
  { value: 'possible', label: 'Possible', score: 3 },
  { value: 'likely', label: 'Likely', score: 4 },
  { value: 'frequent', label: 'Frequent', score: 5 },
];

// Severity options
const SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Minor', score: 1 },
  { value: 'moderate', label: 'Moderate', score: 2 },
  { value: 'significant', label: 'Significant', score: 3 },
  { value: 'major', label: 'Major', score: 4 },
  { value: 'catastrophic', label: 'Catastrophic', score: 5 },
];

// Risk Rating options
const RATING_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-green-500' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
  { value: 'high', label: 'High', color: 'bg-orange-500' },
  { value: 'critical', label: 'Critical', color: 'bg-red-500' },
];

// Status options
const STATUS_OPTIONS = [
  { value: 'identified', label: 'Identified', color: 'bg-blue-500', icon: Eye },
  { value: 'accepted', label: 'Accepted', color: 'bg-gray-500', icon: CheckCircle2 },
  { value: 'mitigated', label: 'Mitigated', color: 'bg-green-500', icon: Shield },
  { value: 'escalating', label: 'Escalating', color: 'bg-red-500', icon: TrendingUp },
  { value: 'closed', label: 'Closed', color: 'bg-slate-500', icon: XCircle },
];

// Mitigation Status options
const MITIGATION_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'implemented', label: 'Implemented' },
  { value: 'verified', label: 'Verified' },
];

// Exposure types
const EXPOSURE_TYPES = [
  { value: 'people', label: 'People' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'production', label: 'Production' },
  { value: 'environment', label: 'Environment' },
  { value: 'cost', label: 'Cost' },
];

// Business criticality
const CRITICALITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Document types
const DOC_TYPES = [
  { value: 'inspection_finding', label: 'Inspection Finding' },
  { value: 'incident_report', label: 'Incident Report' },
  { value: 'alarm_data', label: 'Alarm Data' },
  { value: 'photo', label: 'Photo' },
  { value: 'observation', label: 'Observation' },
];

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border rounded-lg">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <span className="font-medium text-sm">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {isOpen && <div className="p-3 pt-0 border-t">{children}</div>}
    </div>
  );
};

export function RiskExposurePanel({ 
  isOpen,
  onClose,
  currentSweep,
  cameraPosition,
  onNavigate
}) {
  const { token } = useAuth();
  const { currentTour } = useTour();
  const [items, setItems] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docType, setDocType] = useState('photo');
  const fileInputRef = useRef(null);

  // Form state
  const initialFormState = {
    title: '',
    description: '',
    category: 'operational',
    risk_type: '',
    building_id: '',
    floor_area: '',
    related_assets: '',
    related_instruments: '',
    related_process: '',
    // Assessment
    likelihood: '',
    severity: '',
    risk_rating: '',
    exposure_types: [],
    // Status & Timing
    status: 'identified',
    date_identified: '',
    last_reviewed: '',
    next_review: '',
    trigger_condition: '',
    // Ownership
    risk_owner: '',
    responsible_team: '',
    escalation_contact: '',
    approver: '',
    // Mitigation
    existing_controls: '',
    mitigation_plan: '',
    mitigation_status: 'not_started',
    residual_risk_level: '',
    related_planned_changes: '',
    related_workflows: '',
    // Impact
    potential_downtime: '',
    potential_cost: '',
    safety_impact: '',
    environmental_impact: '',
    compliance_impact: '',
    business_criticality: '',
    // Evidence
    incident_history: '',
    instrumentation_notes: '',
    observations: '',
    // Position
    capturePosition: false,
  };
  
  const [formData, setFormData] = useState(initialFormState);

  // Fetch risks
  const fetchItems = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/risks?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Error fetching risks:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  // Fetch buildings
  const fetchBuildings = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/buildings?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setBuildings(await response.json());
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchItems();
      fetchBuildings();
    }
  }, [isOpen, fetchItems, fetchBuildings]);

  // Calculate risk score
  const calculateRiskScore = (likelihood, severity) => {
    const likelihoodScore = LIKELIHOOD_OPTIONS.find(l => l.value === likelihood)?.score || 0;
    const severityScore = SEVERITY_OPTIONS.find(s => s.value === severity)?.score || 0;
    return likelihoodScore * severityScore;
  };

  // Determine risk rating from score
  const getRiskRating = (score) => {
    if (score >= 15) return 'critical';
    if (score >= 10) return 'high';
    if (score >= 5) return 'medium';
    return 'low';
  };

  // Filter items
  const filteredItems = items.filter(item => {
    const matchesSearch = item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.risk_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !currentTour?.id) return;

    setSubmitting(true);
    try {
      const building = buildings.find(b => b.id === formData.building_id);
      const riskScore = calculateRiskScore(formData.likelihood, formData.severity);
      const calculatedRating = formData.risk_rating || getRiskRating(riskScore);
      
      const submitData = {
        title: formData.title,
        description: formData.description || null,
        category: formData.category,
        risk_type: formData.risk_type || null,
        building_id: formData.building_id || null,
        building_name: building?.name || null,
        floor_area: formData.floor_area || null,
        related_assets: formData.related_assets ? formData.related_assets.split(',').map(s => s.trim()) : [],
        related_instruments: formData.related_instruments ? formData.related_instruments.split(',').map(s => s.trim()) : [],
        related_process: formData.related_process || null,
        assessment: {
          likelihood: formData.likelihood || null,
          severity: formData.severity || null,
          risk_score: riskScore || null,
          risk_rating: calculatedRating || null,
          exposure_types: formData.exposure_types || [],
        },
        status: formData.status,
        date_identified: formData.date_identified || null,
        last_reviewed: formData.last_reviewed || null,
        next_review: formData.next_review || null,
        trigger_condition: formData.trigger_condition || null,
        risk_owner: formData.risk_owner || null,
        responsible_team: formData.responsible_team || null,
        escalation_contact: formData.escalation_contact || null,
        approver: formData.approver || null,
        mitigation: {
          existing_controls: formData.existing_controls || null,
          mitigation_plan: formData.mitigation_plan || null,
          mitigation_status: formData.mitigation_status,
          residual_risk_level: formData.residual_risk_level || null,
          related_planned_changes: formData.related_planned_changes ? formData.related_planned_changes.split(',').map(s => s.trim()) : [],
          related_workflows: formData.related_workflows ? formData.related_workflows.split(',').map(s => s.trim()) : [],
        },
        impact: {
          potential_downtime: formData.potential_downtime || null,
          potential_cost: formData.potential_cost ? parseFloat(formData.potential_cost) : null,
          safety_impact: formData.safety_impact || null,
          environmental_impact: formData.environmental_impact || null,
          compliance_impact: formData.compliance_impact || null,
          business_criticality: formData.business_criticality || null,
        },
        incident_history: formData.incident_history || null,
        instrumentation_notes: formData.instrumentation_notes || null,
        observations: formData.observations || null,
        tour_id: currentTour.id,
      };

      if (formData.capturePosition && currentSweep?.id) {
        submitData.sweep_id = currentSweep.id;
        if (cameraPosition?.position) submitData.position = cameraPosition.position;
        if (cameraPosition?.rotation) submitData.rotation = cameraPosition.rotation;
      }

      const url = isEditMode 
        ? `${API_URL}/api/risks/${selectedItem.id}`
        : `${API_URL}/api/risks`;
      
      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        if (isEditMode) {
          const updatedItem = await response.json();
          setItems(prev => prev.map(i => i.id === selectedItem.id ? updatedItem : i));
          setSelectedItem(updatedItem);
        } else {
          await fetchItems();
        }
        handleCloseDialog();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save risk');
      }
    } catch (error) {
      console.error('Error saving risk:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit
  const handleEdit = (item) => {
    setFormData({
      title: item.title || '',
      description: item.description || '',
      category: item.category || 'operational',
      risk_type: item.risk_type || '',
      building_id: item.building_id || '',
      floor_area: item.floor_area || '',
      related_assets: item.related_assets?.join(', ') || '',
      related_instruments: item.related_instruments?.join(', ') || '',
      related_process: item.related_process || '',
      likelihood: item.assessment?.likelihood || '',
      severity: item.assessment?.severity || '',
      risk_rating: item.assessment?.risk_rating || '',
      exposure_types: item.assessment?.exposure_types || [],
      status: item.status || 'identified',
      date_identified: item.date_identified || '',
      last_reviewed: item.last_reviewed || '',
      next_review: item.next_review || '',
      trigger_condition: item.trigger_condition || '',
      risk_owner: item.risk_owner || '',
      responsible_team: item.responsible_team || '',
      escalation_contact: item.escalation_contact || '',
      approver: item.approver || '',
      existing_controls: item.mitigation?.existing_controls || '',
      mitigation_plan: item.mitigation?.mitigation_plan || '',
      mitigation_status: item.mitigation?.mitigation_status || 'not_started',
      residual_risk_level: item.mitigation?.residual_risk_level || '',
      related_planned_changes: item.mitigation?.related_planned_changes?.join(', ') || '',
      related_workflows: item.mitigation?.related_workflows?.join(', ') || '',
      potential_downtime: item.impact?.potential_downtime || '',
      potential_cost: item.impact?.potential_cost || '',
      safety_impact: item.impact?.safety_impact || '',
      environmental_impact: item.impact?.environmental_impact || '',
      compliance_impact: item.impact?.compliance_impact || '',
      business_criticality: item.impact?.business_criticality || '',
      incident_history: item.incident_history || '',
      instrumentation_notes: item.instrumentation_notes || '',
      observations: item.observations || '',
      capturePosition: !!item.sweep_id,
    });
    setIsEditMode(true);
    setIsDetailOpen(false);
    setIsCreateDialogOpen(true);
  };

  // Handle status update
  const handleStatusUpdate = async (itemId, newStatus) => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/risks/${itemId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        const updatedItem = await response.json();
        setItems(prev => prev.map(i => i.id === itemId ? updatedItem : i));
        if (selectedItem?.id === itemId) setSelectedItem(updatedItem);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Handle add comment
  const handleAddComment = async () => {
    if (!token || !selectedItem || !newComment.trim()) return;
    try {
      const response = await fetch(
        `${API_URL}/api/risks/${selectedItem.id}/activity?action=comment&details=${encodeURIComponent(newComment.trim())}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const updatedItem = await response.json();
        setSelectedItem(updatedItem);
        setItems(prev => prev.map(i => i.id === selectedItem.id ? updatedItem : i));
        setNewComment('');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Handle document upload
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !token || !selectedItem) return;
    if (file.size > 15 * 1024 * 1024) { alert('File too large. Max 15MB.'); return; }

    setUploadingDoc(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      
      const response = await fetch(
        `${API_URL}/api/risks/${selectedItem.id}/documents/upload?doc_type=${docType}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formDataObj }
      );
      if (response.ok) {
        const updatedItem = await response.json();
        setSelectedItem(updatedItem);
        setItems(prev => prev.map(i => i.id === selectedItem.id ? updatedItem : i));
      }
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle document delete
  const handleDeleteDoc = async (docId) => {
    if (!token || !selectedItem || !window.confirm('Delete this document?')) return;
    try {
      const response = await fetch(
        `${API_URL}/api/risks/${selectedItem.id}/documents/${docId}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const updatedDocs = selectedItem.documents.filter(d => d.id !== docId);
        const updatedItem = { ...selectedItem, documents: updatedDocs };
        setSelectedItem(updatedItem);
        setItems(prev => prev.map(i => i.id === selectedItem.id ? updatedItem : i));
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  // Handle delete
  const handleDelete = async (itemId) => {
    if (!token || !window.confirm('Delete this risk?')) return;
    try {
      const response = await fetch(`${API_URL}/api/risks/${itemId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setItems(prev => prev.filter(i => i.id !== itemId));
        if (selectedItem?.id === itemId) { setSelectedItem(null); setIsDetailOpen(false); }
      }
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setIsEditMode(false);
    setFormData(initialFormState);
  };

  // Download report with authentication
  const handleDownloadReport = async (riskId, riskIdDisplay) => {
    try {
      const response = await fetch(`${API_URL}/api/risks/${riskId}/report`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate report');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${riskIdDisplay}_report.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Failed to download report');
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    if (!option) return null;
    const Icon = option.icon;
    return (
      <Badge className={`${option.color} text-white gap-1`}>
        <Icon className="h-3 w-3" />
        {option.label}
      </Badge>
    );
  };

  const getRatingBadge = (rating) => {
    const option = RATING_OPTIONS.find(r => r.value === rating);
    if (!option) return null;
    return (
      <Badge className={`${option.color} text-white`}>
        {option.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : null;
  const formatTimestamp = (dateStr) => dateStr ? new Date(dateStr).toLocaleString() : '';
  const formatCurrency = (amount) => amount ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount) : null;

  // Calculate stats
  const stats = {
    identified: items.filter(i => i.status === 'identified').length,
    mitigated: items.filter(i => i.status === 'mitigated').length,
    critical: items.filter(i => i.assessment?.risk_rating === 'critical').length,
    high: items.filter(i => i.assessment?.risk_rating === 'high').length,
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-[400px] sm:w-[540px] p-0">
          <SheetHeader className="p-6 pb-4">
            <SheetTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-primary" />
              Risk & Exposure
            </SheetTitle>
            <SheetDescription>Identify, assess, and mitigate risks across the facility</SheetDescription>
          </SheetHeader>

          <div className="px-6 pb-4 space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-2">
              <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center">
                <p className="text-lg font-bold text-blue-600">{stats.identified}</p>
                <p className="text-[10px] text-muted-foreground">Identified</p>
              </div>
              <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                <p className="text-lg font-bold text-green-600">{stats.mitigated}</p>
                <p className="text-[10px] text-muted-foreground">Mitigated</p>
              </div>
              <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
                <p className="text-lg font-bold text-red-600">{stats.critical}</p>
                <p className="text-[10px] text-muted-foreground">Critical</p>
              </div>
              <div className="p-2 rounded-lg bg-orange-500/10 border border-orange-500/20 text-center">
                <p className="text-lg font-bold text-orange-600">{stats.high}</p>
                <p className="text-[10px] text-muted-foreground">High</p>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search risks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button onClick={() => { setIsEditMode(false); setFormData(initialFormState); setIsCreateDialogOpen(true); }}>
                <Plus className="h-4 w-4 mr-1" />New
              </Button>
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Items List */}
          <ScrollArea className="h-[calc(100vh-340px)] px-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No risks found</p>
                <p className="text-sm">Add a risk to start tracking exposure</p>
              </div>
            ) : (
              <div className="space-y-2 pb-4">
                {filteredItems.map((item) => (
                  <div
                    key={item.id}
                    data-testid={`risk-item-${item.risk_id}`}
                    className="p-3 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
                    onClick={() => { setSelectedItem(item); setIsDetailOpen(true); }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-muted-foreground">{item.risk_id}</span>
                          {getStatusBadge(item.status)}
                          {item.assessment?.risk_rating && getRatingBadge(item.assessment.risk_rating)}
                        </div>
                        <p className="font-medium truncate">{item.title}</p>
                        {item.description && (
                          <p className="text-sm text-muted-foreground truncate mt-1">{item.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-[10px]">
                            {CATEGORY_OPTIONS.find(c => c.value === item.category)?.label || item.category}
                          </Badge>
                          {item.building_name && (
                            <span className="flex items-center gap-1">
                              <Building className="h-3 w-3" />
                              {item.building_name}
                            </span>
                          )}
                          {item.sweep_id && (
                            <Eye className="h-3 w-3 text-primary" title="Has 3D position" />
                          )}
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="flex-shrink-0" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          handleDownloadReport(item.id, item.risk_id);
                        }} 
                        title="Download Report"
                      >
                        <FileDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Edit Risk' : 'New Risk'}</DialogTitle>
            <DialogDescription>
              {isEditMode ? 'Update risk details and assessment' : 'Identify and document a new risk'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Brief risk description"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Detailed description of the risk"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={formData.risk_type || 'none'} onValueChange={(value) => setFormData({...formData, risk_type: value === 'none' ? '' : value})}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {TYPE_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Location */}
            <CollapsibleSection title="Location & Context" icon={Building} defaultOpen={true}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Building</Label>
                    <Select value={formData.building_id || 'none'} onValueChange={(value) => setFormData({...formData, building_id: value === 'none' ? '' : value})}>
                      <SelectTrigger><SelectValue placeholder="Select building" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Floor / Area</Label>
                    <Input value={formData.floor_area} onChange={(e) => setFormData({...formData, floor_area: e.target.value})} placeholder="e.g., Level 2, Mezzanine" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Related Assets (comma-separated)</Label>
                  <Input value={formData.related_assets} onChange={(e) => setFormData({...formData, related_assets: e.target.value})} placeholder="e.g., Boiler-01, Pump-A2" />
                </div>
                <div className="space-y-2">
                  <Label>Related Instruments (comma-separated)</Label>
                  <Input value={formData.related_instruments} onChange={(e) => setFormData({...formData, related_instruments: e.target.value})} placeholder="e.g., PT-101, TT-205" />
                </div>
                <div className="space-y-2">
                  <Label>Related Process</Label>
                  <Input value={formData.related_process} onChange={(e) => setFormData({...formData, related_process: e.target.value})} placeholder="e.g., Steam Generation" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Assessment */}
            <CollapsibleSection title="Risk Assessment" icon={Gauge} defaultOpen={true}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Likelihood</Label>
                    <Select value={formData.likelihood || 'none'} onValueChange={(value) => setFormData({...formData, likelihood: value === 'none' ? '' : value})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Set</SelectItem>
                        {LIKELIHOOD_OPTIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label} ({l.score})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Severity</Label>
                    <Select value={formData.severity || 'none'} onValueChange={(value) => setFormData({...formData, severity: value === 'none' ? '' : value})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Set</SelectItem>
                        {SEVERITY_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label} ({s.score})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.likelihood && formData.severity && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-sm">
                      <span className="text-muted-foreground">Calculated Score: </span>
                      <span className="font-bold">{calculateRiskScore(formData.likelihood, formData.severity)}</span>
                      <span className="text-muted-foreground"> → </span>
                      <Badge className={`${RATING_OPTIONS.find(r => r.value === getRiskRating(calculateRiskScore(formData.likelihood, formData.severity)))?.color} text-white`}>
                        {getRiskRating(calculateRiskScore(formData.likelihood, formData.severity)).toUpperCase()}
                      </Badge>
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Override Risk Rating (optional)</Label>
                  <Select value={formData.risk_rating || 'none'} onValueChange={(value) => setFormData({...formData, risk_rating: value === 'none' ? '' : value})}>
                    <SelectTrigger><SelectValue placeholder="Auto-calculated" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Auto-calculate</SelectItem>
                      {RATING_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Exposure Types</Label>
                  <div className="flex flex-wrap gap-2">
                    {EXPOSURE_TYPES.map(et => (
                      <Button
                        key={et.value}
                        type="button"
                        variant={formData.exposure_types.includes(et.value) ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          const current = formData.exposure_types || [];
                          const updated = current.includes(et.value)
                            ? current.filter(v => v !== et.value)
                            : [...current, et.value];
                          setFormData({...formData, exposure_types: updated});
                        }}
                      >
                        {et.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Status & Timing */}
            <CollapsibleSection title="Status & Timing" icon={Clock}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Date Identified</Label>
                    <Input type="date" value={formData.date_identified} onChange={(e) => setFormData({...formData, date_identified: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Last Reviewed</Label>
                    <Input type="date" value={formData.last_reviewed} onChange={(e) => setFormData({...formData, last_reviewed: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Next Review</Label>
                    <Input type="date" value={formData.next_review} onChange={(e) => setFormData({...formData, next_review: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Trigger Condition</Label>
                  <Input value={formData.trigger_condition} onChange={(e) => setFormData({...formData, trigger_condition: e.target.value})} placeholder="When does this risk escalate?" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Ownership */}
            <CollapsibleSection title="Ownership & Accountability" icon={Users}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Risk Owner</Label>
                    <Input value={formData.risk_owner} onChange={(e) => setFormData({...formData, risk_owner: e.target.value})} placeholder="Name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Responsible Team</Label>
                    <Input value={formData.responsible_team} onChange={(e) => setFormData({...formData, responsible_team: e.target.value})} placeholder="e.g., Operations" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Escalation Contact</Label>
                    <Input value={formData.escalation_contact} onChange={(e) => setFormData({...formData, escalation_contact: e.target.value})} placeholder="Who to escalate to?" />
                  </div>
                  <div className="space-y-2">
                    <Label>Approver</Label>
                    <Input value={formData.approver} onChange={(e) => setFormData({...formData, approver: e.target.value})} placeholder="Who signs off?" />
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Mitigation */}
            <CollapsibleSection title="Mitigation & Controls" icon={Shield}>
              <div className="space-y-3 mt-3">
                <div className="space-y-2">
                  <Label>Existing Controls</Label>
                  <Textarea value={formData.existing_controls} onChange={(e) => setFormData({...formData, existing_controls: e.target.value})} placeholder="What controls are currently in place?" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Mitigation Plan</Label>
                  <Textarea value={formData.mitigation_plan} onChange={(e) => setFormData({...formData, mitigation_plan: e.target.value})} placeholder="What additional actions are needed?" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mitigation Status</Label>
                    <Select value={formData.mitigation_status} onValueChange={(value) => setFormData({...formData, mitigation_status: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MITIGATION_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Residual Risk Level</Label>
                    <Select value={formData.residual_risk_level || 'none'} onValueChange={(value) => setFormData({...formData, residual_risk_level: value === 'none' ? '' : value})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Set</SelectItem>
                        {RATING_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Related Planned Changes (comma-separated IDs)</Label>
                  <Input value={formData.related_planned_changes} onChange={(e) => setFormData({...formData, related_planned_changes: e.target.value})} placeholder="e.g., CHG-0001, CHG-0002" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Impact */}
            <CollapsibleSection title="Impact & Exposure" icon={DollarSign}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Potential Downtime</Label>
                    <Input value={formData.potential_downtime} onChange={(e) => setFormData({...formData, potential_downtime: e.target.value})} placeholder="e.g., 8 hours" />
                  </div>
                  <div className="space-y-2">
                    <Label>Potential Cost ($)</Label>
                    <Input type="number" value={formData.potential_cost} onChange={(e) => setFormData({...formData, potential_cost: e.target.value})} placeholder="0.00" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Safety Impact</Label>
                  <Textarea value={formData.safety_impact} onChange={(e) => setFormData({...formData, safety_impact: e.target.value})} placeholder="Describe safety implications" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Environmental Impact</Label>
                  <Textarea value={formData.environmental_impact} onChange={(e) => setFormData({...formData, environmental_impact: e.target.value})} placeholder="Describe environmental implications" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Compliance Impact</Label>
                  <Textarea value={formData.compliance_impact} onChange={(e) => setFormData({...formData, compliance_impact: e.target.value})} placeholder="Any regulatory implications?" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Business Criticality</Label>
                  <Select value={formData.business_criticality || 'none'} onValueChange={(value) => setFormData({...formData, business_criticality: value === 'none' ? '' : value})}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not Set</SelectItem>
                      {CRITICALITY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CollapsibleSection>

            {/* Evidence */}
            <CollapsibleSection title="Evidence & History" icon={FileText}>
              <div className="space-y-3 mt-3">
                <div className="space-y-2">
                  <Label>Incident History</Label>
                  <Textarea value={formData.incident_history} onChange={(e) => setFormData({...formData, incident_history: e.target.value})} placeholder="Previous related incidents" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Instrumentation Notes</Label>
                  <Textarea value={formData.instrumentation_notes} onChange={(e) => setFormData({...formData, instrumentation_notes: e.target.value})} placeholder="Relevant alarm or sensor data" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Observations</Label>
                  <Textarea value={formData.observations} onChange={(e) => setFormData({...formData, observations: e.target.value})} placeholder="Field observations" rows={2} />
                </div>
              </div>
            </CollapsibleSection>

            {/* 3D Position */}
            <div className="p-3 rounded-lg border space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" />Link to 3D Position</Label>
                <Button type="button" variant={formData.capturePosition ? "default" : "outline"} size="sm" onClick={() => setFormData(prev => ({ ...prev, capturePosition: !prev.capturePosition }))}>
                  {formData.capturePosition ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              {formData.capturePosition ? (
                currentSweep?.id ? (
                  <p className="text-xs text-green-600">✓ Current camera position will be saved</p>
                ) : (
                  <p className="text-xs text-yellow-600">⚠ Navigate to a location in the 3D viewer first</p>
                )
              ) : (
                isEditMode && selectedItem?.sweep_id && (
                  <p className="text-xs text-orange-600">⚠ Saving will clear the existing position</p>
                )
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isEditMode ? 'Save Changes' : 'Create Risk'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedItem && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="font-mono">{selectedItem.risk_id}</span>
                    {getStatusBadge(selectedItem.status)}
                    {selectedItem.assessment?.risk_rating && getRatingBadge(selectedItem.assessment.risk_rating)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDownloadReport(selectedItem.id, selectedItem.risk_id)}
                    >
                      <FileDown className="h-4 w-4 mr-2" />Report
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(selectedItem)}>
                      <Edit3 className="h-4 w-4 mr-2" />Edit
                    </Button>
                  </div>
                </div>
                <DialogTitle>{selectedItem.title}</DialogTitle>
                {selectedItem.description && (
                  <p className="text-sm text-muted-foreground">{selectedItem.description}</p>
                )}
              </DialogHeader>

              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="mitigation">Mitigation</TabsTrigger>
                  <TabsTrigger value="evidence">Evidence ({selectedItem.documents?.length || 0})</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="flex items-center gap-2">
                    <Label>Status:</Label>
                    <Select value={selectedItem.status} onValueChange={(value) => handleStatusUpdate(selectedItem.id, value)}>
                      <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><Label className="text-muted-foreground">Category</Label><p>{CATEGORY_OPTIONS.find(c => c.value === selectedItem.category)?.label}</p></div>
                    {selectedItem.risk_type && <div><Label className="text-muted-foreground">Type</Label><p>{TYPE_OPTIONS.find(t => t.value === selectedItem.risk_type)?.label}</p></div>}
                    {selectedItem.building_name && <div><Label className="text-muted-foreground">Building</Label><p>{selectedItem.building_name}</p></div>}
                    {selectedItem.floor_area && <div><Label className="text-muted-foreground">Floor/Area</Label><p>{selectedItem.floor_area}</p></div>}
                  </div>

                  {/* Assessment Section */}
                  {selectedItem.assessment && (
                    <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                      <Label className="font-semibold">Risk Assessment</Label>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        {selectedItem.assessment.likelihood && (
                          <div><span className="text-muted-foreground">Likelihood:</span> {LIKELIHOOD_OPTIONS.find(l => l.value === selectedItem.assessment.likelihood)?.label}</div>
                        )}
                        {selectedItem.assessment.severity && (
                          <div><span className="text-muted-foreground">Severity:</span> {SEVERITY_OPTIONS.find(s => s.value === selectedItem.assessment.severity)?.label}</div>
                        )}
                        {selectedItem.assessment.risk_score && (
                          <div><span className="text-muted-foreground">Score:</span> <span className="font-bold">{selectedItem.assessment.risk_score}</span></div>
                        )}
                      </div>
                      {selectedItem.assessment.exposure_types?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {selectedItem.assessment.exposure_types.map(et => (
                            <Badge key={et} variant="outline" className="text-xs">{EXPOSURE_TYPES.find(e => e.value === et)?.label || et}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Ownership */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {selectedItem.risk_owner && <div><Label className="text-muted-foreground">Risk Owner</Label><p>{selectedItem.risk_owner}</p></div>}
                    {selectedItem.responsible_team && <div><Label className="text-muted-foreground">Team</Label><p>{selectedItem.responsible_team}</p></div>}
                    {selectedItem.date_identified && <div><Label className="text-muted-foreground">Identified</Label><p>{formatDate(selectedItem.date_identified)}</p></div>}
                    {selectedItem.next_review && <div><Label className="text-muted-foreground">Next Review</Label><p>{formatDate(selectedItem.next_review)}</p></div>}
                  </div>

                  {/* Impact */}
                  {selectedItem.impact?.potential_cost && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-200">
                      <Label className="text-red-600">Potential Cost Impact</Label>
                      <p className="text-lg font-bold text-red-600">{formatCurrency(selectedItem.impact.potential_cost)}</p>
                      {selectedItem.impact.potential_downtime && (
                        <p className="text-sm text-muted-foreground">Potential downtime: {selectedItem.impact.potential_downtime}</p>
                      )}
                    </div>
                  )}

                  {selectedItem.sweep_id && (
                    <Button variant="outline" className="w-full" onClick={() => { onNavigate?.(selectedItem.sweep_id, { rotation: selectedItem.rotation, transitionTime: 1500 }); setIsDetailOpen(false); onClose(); }}>
                      <MapPin className="h-4 w-4 mr-2" />Go to Location
                    </Button>
                  )}
                </TabsContent>

                <TabsContent value="mitigation" className="mt-4 space-y-4">
                  {selectedItem.mitigation ? (
                    <>
                      <div className="flex items-center gap-2">
                        <Label>Mitigation Status:</Label>
                        <Badge variant="outline">{MITIGATION_STATUS_OPTIONS.find(s => s.value === selectedItem.mitigation.mitigation_status)?.label || 'Not Started'}</Badge>
                        {selectedItem.mitigation.residual_risk_level && (
                          <>
                            <Label className="ml-4">Residual Risk:</Label>
                            {getRatingBadge(selectedItem.mitigation.residual_risk_level)}
                          </>
                        )}
                      </div>
                      {selectedItem.mitigation.existing_controls && (
                        <div><Label className="text-muted-foreground">Existing Controls</Label><p className="mt-1 text-sm">{selectedItem.mitigation.existing_controls}</p></div>
                      )}
                      {selectedItem.mitigation.mitigation_plan && (
                        <div><Label className="text-muted-foreground">Mitigation Plan</Label><p className="mt-1 text-sm">{selectedItem.mitigation.mitigation_plan}</p></div>
                      )}
                      {selectedItem.mitigation.related_planned_changes?.length > 0 && (
                        <div>
                          <Label className="text-muted-foreground">Related Planned Changes</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {selectedItem.mitigation.related_planned_changes.map(id => (
                              <Badge key={id} variant="outline">{id}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No mitigation plan defined</p>
                      <p className="text-sm">Edit this risk to add controls and mitigation</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="evidence" className="mt-4">
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Select value={docType} onValueChange={setDocType}>
                        <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <input type="file" ref={fileInputRef} onChange={handleDocUpload} className="hidden" />
                      <Button onClick={() => fileInputRef.current?.click()} disabled={uploadingDoc} className="flex-1">
                        {uploadingDoc ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="h-4 w-4 mr-2" />Upload</>}
                      </Button>
                    </div>

                    {selectedItem.documents?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedItem.documents.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border">
                            <div className="flex items-center gap-3">
                              {doc.url?.startsWith('data:image') ? <FileImage className="h-5 w-5 text-blue-500" /> : <File className="h-5 w-5 text-gray-500" />}
                              <div>
                                <p className="font-medium text-sm">{doc.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {DOC_TYPES.find(t => t.value === doc.doc_type)?.label} • {doc.uploaded_by_name} • {formatTimestamp(doc.uploaded_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {doc.url?.startsWith('data:image') && <Button variant="ghost" size="icon" onClick={() => setSelectedDoc(doc)}><ZoomIn className="h-4 w-4" /></Button>}
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(doc.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No evidence uploaded yet</p>
                        <p className="text-sm">Add inspection findings, photos, or reports</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="activity" className="mt-4">
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input placeholder="Add a comment..." value={newComment} onChange={(e) => setNewComment(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddComment()} />
                      <Button onClick={handleAddComment} disabled={!newComment.trim()}><Send className="h-4 w-4" /></Button>
                    </div>

                    <ScrollArea className="h-[300px]">
                      <div className="space-y-3">
                        {selectedItem.activity?.slice().reverse().map(activity => (
                          <div key={activity.id} className="p-3 rounded-lg bg-muted/50">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{activity.user_name}</span>
                              <span className="text-xs text-muted-foreground">{formatTimestamp(activity.timestamp)}</span>
                            </div>
                            <p className="text-sm">
                              {activity.action === 'comment' ? activity.details : activity.action === 'status_changed' ? (
                                <span>Changed status from <Badge variant="outline">{activity.old_value}</Badge> to <Badge variant="outline">{activity.new_value}</Badge></span>
                              ) : (activity.details || activity.action.replace(/_/g, ' '))}
                            </p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedItem.id)}><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent className="max-w-3xl p-0">
          {selectedDoc && (
            <div className="relative">
              <img src={selectedDoc.url} alt={selectedDoc.name} className="w-full max-h-[80vh] object-contain" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-medium">{selectedDoc.name}</p>
                <p className="text-white/70 text-sm">Uploaded by {selectedDoc.uploaded_by_name} • {formatTimestamp(selectedDoc.uploaded_at)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default RiskExposurePanel;
